// © Microsoft Corporation. All rights reserved.

import { TimestampType } from "./TimestampType";

const SECONDS_IN_ONE_HOUR = 3600;
const SECONDS_IN_TWO_MINUTE: number = 2 * 60;
const REG_OFFSET_MINS = new RegExp("\\:([\\d]{2})");

/**
 * Get current date
 */
function getCurrentDate(): Date {
    return new Date();
}

/**
 * Claculates the difference between date provided and now in seconds.
 * @param dateFrom date from which difference needs to be calculated
 */
export function timespanInSeconds(dateFrom: Date): number {
    const dateTo = getCurrentDate();
    return (dateTo.getTime() - dateFrom.getTime()) / 1000;
}

/**
 * Returns the local midnight time.
 */
function getLocalMidnight(): Date {
    const midnight = getCurrentDate();
    midnight.setHours(0, 0, 0, 0);
    return midnight;
}

/**
 * Returns the start of the year.
 */
function getStartOfYear(): Date {
    const now = getCurrentDate();
    return new Date(now.getFullYear(), 0, 0, 0, 0, 0, 0);
}

/**
 * Function to add days to the date provided.
 * @param day Date to which days are to be added
 * @param days number of days to add
 */
export function addDays(day: Date, days: number): Date {
    return new Date(day.getFullYear(), day.getMonth(), day.getDate() + days, day.getHours(), day.getMinutes(), day.getSeconds(), day.getMilliseconds());
}

/**
 * add hours to the start date and return the result date
 * @param start Date to which hours are to be added
 * @param hours number of hours
 */
export function addHours(start: Date, hours: number): Date {
    return new Date(start.valueOf() + (hours * 60 * 60 * 1000));
}

/**
 * Add seconds to the current date
 * @param day Date to add days to
 * @param seconds Number of seconds to add
 */
export function addSeconds(day: Date, seconds: number): Date {
    return new Date(day.getFullYear(), day.getMonth(), day.getDate(), day.getHours(), day.getMinutes(), day.getSeconds() + seconds, day.getMilliseconds());
}

/**
 * convert date to string with specified timezone offset
 * @param date {Date}
 * @param utcOffset {string} eg. -07:00, 08:00:00, -02:30
 */
export function toISOString(date: Date, utcOffset: string): string {
    const offsetHours = parseInt(utcOffset);
    const offsetMins = parseInt(REG_OFFSET_MINS.exec(utcOffset)[1]);
    const sign = offsetHours < 0 ? -1 : 1;
    const adjust = new Date(date.getTime() + (offsetHours * 60 + sign * offsetMins) * 60 * 1000);
    const offsetString = `0${Math.abs(offsetHours)}`.substr(-2) + ":" + `0${Math.abs(offsetMins)}`.substr(-2);
    return adjust.toISOString().replace("Z", (sign < 0 ? "-" : "+") + offsetString);
}

/**
 * Returns the timestamp time based on how old the provided date is.
 * @param date Date for which timestamp type has to be returned
 */
export function getTimestampType(date: Date): TimestampType {
    const timespan = timespanInSeconds(date);
    const midnight = getLocalMidnight();
    const yesterday = addDays(midnight, -1);
    const startOfWeek = addDays(midnight, -6);
    const startOfYear = getStartOfYear();

    if (timespan >= -60 * 55 && timespan < -60 * 1) { /* between 55 min and 1 min in future */
        return TimestampType.InMinutes;
    } else if (timespan < 0) {
        return TimestampType.None;
    } else if (timespan < SECONDS_IN_TWO_MINUTE) { /* less than 2 minutes */
        return TimestampType.JustNow;
    } else if (timespan < SECONDS_IN_ONE_HOUR) {   /* 2 minutes - 1 hour */
        return TimestampType.LessThanOneHour;
    } else if (date >= midnight) { /* Today */
        return TimestampType.Today;
    } else if (date >= yesterday) { /* Yesterday */
        return TimestampType.Yesterday;
    } else if (date >= startOfWeek) { /* In current week */
        return TimestampType.ThisWeek;
    } else if (date >= startOfYear) { /* In current year */
        return TimestampType.ThisYear;
    } else {
        return TimestampType.FullDate;
    }
}